import React, { useCallback } from 'react'
import styled from 'styled-components'

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  border-top-left-radius: ${({ theme }) => theme.radii.md};
  border-top-right-radius: ${({ theme }) => theme.radii.md};
`

const Group = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`

const Separator = styled.span`
  width: 1px;
  height: 20px;
  background: ${({ theme }) => theme.colors.border};
  margin: 0 4px;
`

const Btn = styled.button.attrs({ type: 'button' })`
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.radii.sm};
  background: ${(p) => (p.$active ? `${p.theme.colors.primary}1F` : 'transparent')};
  color: ${(p) => (p.$active ? p.theme.colors.primary : p.theme.colors.muted)};
  cursor: pointer;
  padding: 0;
  transition: background 0.15s ease, color 0.15s ease;
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.text};
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  svg { width: 16px; height: 16px; display: block; }
`

const Icon = ({ children }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
)

const icons = {
  bold: <Icon><path d="M14 12a4 4 0 0 0 0-8H6v8" /><path d="M15 20a4 4 0 0 0 0-8H6v8z" /></Icon>,
  italic: <Icon><line x1="19" y1="4" x2="10" y2="4" /><line x1="14" y1="20" x2="5" y2="20" /><line x1="15" y1="4" x2="9" y2="20" /></Icon>,
  underline: <Icon><path d="M6 4v6a6 6 0 0 0 12 0V4" /><line x1="4" y1="20" x2="20" y2="20" /></Icon>,
  strike: <Icon><path d="M16 4H9a3 3 0 0 0-2.83 4" /><path d="M14 12a4 4 0 0 1 0 8H6" /><line x1="4" y1="12" x2="20" y2="12" /></Icon>,
  code: <Icon><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></Icon>,
  paragraph: <Icon><path d="M13 4v16" /><path d="M17 4v16" /><path d="M19 4H9.5a4.5 4.5 0 0 0 0 9H13" /></Icon>,
  h1: <Icon><path d="M4 12h8" /><path d="M4 18V6" /><path d="M12 18V6" /><path d="m17 12 3-2v8" /></Icon>,
  h2: <Icon><path d="M4 12h8" /><path d="M4 18V6" /><path d="M12 18V6" /><path d="M21 18h-4c0-4 4-3 4-6 0-1.5-2-2.5-4-1" /></Icon>,
  h3: <Icon><path d="M4 12h8" /><path d="M4 18V6" /><path d="M12 18V6" /><path d="M17.5 10.5c1.7-1 3.5 0 3.5 1.5a2 2 0 0 1-2 2" /><path d="M17 17.5c2 1.5 4 .3 4-1.5a2 2 0 0 0-2-2" /></Icon>,
  bulletList: <Icon><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></Icon>,
  orderedList: <Icon><line x1="10" y1="6" x2="21" y2="6" /><line x1="10" y1="12" x2="21" y2="12" /><line x1="10" y1="18" x2="21" y2="18" /><path d="M4 6h1v4" /><path d="M4 10h2" /><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" /></Icon>,
  blockquote: <Icon><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" /><path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z" /></Icon>,
  link: <Icon><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></Icon>,
  clear: <Icon><path d="M16 3h5v5" /><path d="M8 3H3v5" /><path d="M12 22v-8.3a4 4 0 0 0-1.172-2.872L3 3" /><path d="m15 9 6-6" /></Icon>,
  undo: <Icon><path d="M3 7v6h6" /><path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" /></Icon>,
  redo: <Icon><path d="M21 7v6h-6" /><path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3l3 2.7" /></Icon>,
  alignLeft: <Icon><line x1="21" y1="6" x2="3" y2="6" /><line x1="15" y1="10" x2="3" y2="10" /><line x1="21" y1="14" x2="3" y2="14" /><line x1="15" y1="18" x2="3" y2="18" /></Icon>,
  alignCenter: <Icon><line x1="21" y1="6" x2="3" y2="6" /><line x1="18" y1="10" x2="6" y2="10" /><line x1="21" y1="14" x2="3" y2="14" /><line x1="18" y1="18" x2="6" y2="18" /></Icon>,
  alignRight: <Icon><line x1="21" y1="6" x2="3" y2="6" /><line x1="21" y1="10" x2="9" y2="10" /><line x1="21" y1="14" x2="3" y2="14" /><line x1="21" y1="18" x2="9" y2="18" /></Icon>,
  alignJustify: <Icon><line x1="21" y1="6" x2="3" y2="6" /><line x1="21" y1="10" x2="3" y2="10" /><line x1="21" y1="14" x2="3" y2="14" /><line x1="21" y1="18" x2="3" y2="18" /></Icon>
}

// Allineamento del blocco: feature → valore dell'attributo textAlign (extensions.js).
const ALIGN_FEATURES = {
  alignLeft: 'left',
  alignCenter: 'center',
  alignRight: 'right',
  alignJustify: 'justify'
}

const ALL_FEATURES = [
  'bold', 'italic', 'underline', 'strike', 'code',
  'paragraph', 'h1', 'h2', 'h3',
  'alignLeft', 'alignCenter', 'alignRight', 'alignJustify',
  'bulletList', 'orderedList', 'blockquote',
  'link', 'clearFormatting', 'undo', 'redo'
]

const GROUPS = [
  ['bold', 'italic', 'underline', 'strike', 'code'],
  ['paragraph', 'h1', 'h2', 'h3'],
  ['alignLeft', 'alignCenter', 'alignRight', 'alignJustify'],
  ['bulletList', 'orderedList', 'blockquote'],
  ['link', 'clearFormatting'],
  ['undo', 'redo']
]

const labels = {
  bold: 'Grassetto', italic: 'Corsivo', underline: 'Sottolineato', strike: 'Barrato', code: 'Codice inline',
  paragraph: 'Paragrafo', h1: 'Titolo 1', h2: 'Titolo 2', h3: 'Titolo 3',
  alignLeft: 'Allinea a sinistra', alignCenter: 'Centra', alignRight: 'Allinea a destra', alignJustify: 'Giustifica',
  bulletList: 'Elenco puntato', orderedList: 'Elenco numerato', blockquote: 'Citazione',
  link: 'Link', clearFormatting: 'Rimuovi formattazione', undo: 'Annulla', redo: 'Ripeti'
}

function isActive(editor, feature) {
  if (!editor) return false
  switch (feature) {
    case 'bold': return editor.isActive('bold')
    case 'italic': return editor.isActive('italic')
    case 'underline': return editor.isActive('underline')
    case 'strike': return editor.isActive('strike')
    case 'code': return editor.isActive('code')
    case 'paragraph': return editor.isActive('paragraph')
    case 'h1': return editor.isActive('heading', { level: 1 })
    case 'h2': return editor.isActive('heading', { level: 2 })
    case 'h3': return editor.isActive('heading', { level: 3 })
    case 'bulletList': return editor.isActive('bulletList')
    case 'orderedList': return editor.isActive('orderedList')
    case 'blockquote': return editor.isActive('blockquote')
    case 'link': return editor.isActive('link')
    default:
      if (feature in ALIGN_FEATURES) return editor.isActive({ textAlign: ALIGN_FEATURES[feature] })
      return false
  }
}

function runFeature(editor, feature) {
  if (!editor) return
  const c = editor.chain().focus()
  switch (feature) {
    case 'bold': c.toggleBold().run(); break
    case 'italic': c.toggleItalic().run(); break
    case 'underline': c.toggleUnderline().run(); break
    case 'strike': c.toggleStrike().run(); break
    case 'code': c.toggleCode().run(); break
    case 'paragraph': c.setParagraph().run(); break
    case 'h1': c.toggleHeading({ level: 1 }).run(); break
    case 'h2': c.toggleHeading({ level: 2 }).run(); break
    case 'h3': c.toggleHeading({ level: 3 }).run(); break
    case 'bulletList': c.toggleBulletList().run(); break
    case 'orderedList': c.toggleOrderedList().run(); break
    case 'blockquote': c.toggleBlockquote().run(); break
    case 'clearFormatting': c.unsetAllMarks().clearNodes().unsetTextAlign().run(); break
    case 'undo': c.undo().run(); break
    case 'redo': c.redo().run(); break
    default:
      if (feature in ALIGN_FEATURES) {
        const value = ALIGN_FEATURES[feature]
        // Ripremere l'allineamento attivo lo toglie (torna al default del contenitore).
        if (editor.isActive({ textAlign: value })) c.unsetTextAlign().run()
        else c.setTextAlign(value).run()
      }
      break
  }
}

function iconFor(feature) {
  if (feature === 'clearFormatting') return icons.clear
  return icons[feature]
}

export function Toolbar({ editor, features = ALL_FEATURES }) {
  const enabled = new Set(features)

  const handleLink = useCallback(() => {
    if (!editor) return
    const previous = editor.getAttributes('link').href || ''
    const url = window.prompt('Inserisci URL', previous)
    if (url === null) return
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }, [editor])

  if (!editor) return null

  return (
    <Bar onMouseDown={(e) => e.preventDefault()} role="toolbar" aria-label="Formattazione">
      {GROUPS.map((group, gi) => {
        const visible = group.filter((f) => enabled.has(f))
        if (visible.length === 0) return null
        return (
          <React.Fragment key={gi}>
            {gi > 0 && <Separator aria-hidden="true" />}
            <Group>
              {visible.map((feature) => {
                const onClick = feature === 'link' ? handleLink : () => runFeature(editor, feature)
                let disabled = false
                if (feature === 'undo') disabled = !editor.can().undo()
                if (feature === 'redo') disabled = !editor.can().redo()
                return (
                  <Btn
                    key={feature}
                    $active={isActive(editor, feature)}
                    disabled={disabled}
                    onClick={onClick}
                    aria-label={labels[feature]}
                    title={labels[feature]}
                  >
                    {iconFor(feature)}
                  </Btn>
                )
              })}
            </Group>
          </React.Fragment>
        )
      })}
    </Bar>
  )
}

Toolbar.ALL_FEATURES = ALL_FEATURES
